import cx from 'classnames';
import React, { CSSProperties, HTMLAttributes } from 'react';

export interface IAvatarProps extends HTMLAttributes<HTMLDivElement> {
  src?: string;
  size: number | 'auto';
  dark?: number;
  opacity?: number;
  shape?: AvatarShape;
  imageStyle?: CSSProperties;
  border?: boolean;
  alt?: string;
  contentBox?: boolean;
  badge?: React.ReactNode;
  badgePosition?: AvatarBadgePosition;
  badgeSize?: AvatarBadgeSize;
  badgeProps?: HTMLAttributes<HTMLDivElement>;
  badgeRender?: (badge: React.ReactNode) => React.ReactNode;
}

type AvatarShape = 'circle' | 'square';
type AvatarBadgePosition = 'bottom';
type AvatarBadgeSize = 'x-small' | 'small' | 'medium' | 'large';

export const Avatar = (props: IAvatarProps) => {
  const {
    src,
    size = 32,
    className,
    style,
    dark,
    opacity,
    children,
    shape = 'square',
    imageStyle,
    border = false,
    alt,
    contentBox = false,
    badge,
    badgePosition = 'bottom',
    badgeSize = 'small',
    badgeProps,
    badgeRender,
    ...restProps
  } = props;

  const Wrapper = badge ? 'div' : React.Fragment;

  const badgeComponent = badge ? (
    <div
      {...badgeProps}
      className={cx(
        'rio-avatar__badge',
        `rio-avatar__badge--${badgePosition}`,
        `rio-avatar__badge--${badgeSize}`,
        badgeProps?.className
      )}
    >
      {badge}
    </div>
  ) : null;

  const badgeRenderResult = badgeRender ? badgeRender(badgeComponent) : badgeComponent;

  return (
    <Wrapper {...(badge ? { className: cx('rio-avatar__wrapper'), ...restProps } : {})}>
      <div
        className={cx(
          'rio-avatar',
          {
            'rio-avatar--border': border
          },
          className
        )}
        style={{
          ...(typeof size === 'number' ? { width: size, height: size } : {}),
          ...(typeof opacity === 'undefined' ? {} : { opacity }),
          ...(shape === 'circle' ? { borderRadius: '50%' } : undefined),
          ...(contentBox ? { boxSizing: 'content-box' } : {}),
          ...style
        }}
        {...(badge ? {} : restProps)}
      >
        {dark && (
          <div
            className="rio-avatar__dark"
            style={{
              background: `linear-gradient(rgba(0, 0, 0, ${dark}), rgba(0, 0, 0, ${dark}))`
            }}
          />
        )}
        {src && (
          <div className="rio-avatar__image">
            <img style={imageStyle} src={src} alt={alt} />
          </div>
        )}
        {children}
      </div>

      {badgeRenderResult}
    </Wrapper>
  );
};
